import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import { ImageIcon, ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import {
  deleteProductUpdateImage,
  productUpdateAudienceLabel,
  uploadProductUpdateImage,
  type ProductUpdateImageResponse,
  type ProductUpdateManagementResponse,
} from '../../services/productUpdates';

const markdownImageReference = (fileName: string): string =>
  `![description](${fileName})`;

/** Base name for markdown refs: strip from the first "." (extension). */
const markdownFileNameFromSelectedFile = (fileName: string): string => {
  const dotIndex = fileName.indexOf('.');
  return dotIndex === -1 ? fileName : fileName.slice(0, dotIndex);
};

const MARKDOWN_IMAGE_SRC_PATTERN = /!\[[^\]]*\]\(([^)]+)\)/g;

const markdownReferencesFileName = (
  markdown: string,
  fileName: string
): boolean => {
  for (const match of markdown.matchAll(MARKDOWN_IMAGE_SRC_PATTERN)) {
    const href = match[1]?.trim();
    if (href === fileName) {
      return true;
    }
  }
  return false;
};

const matchesUiSearch = (query: string, ...texts: string[]): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return texts.some(text => text.toLowerCase().includes(needle));
};

const audienceLabelsUsingImage = (
  fileName: string,
  updates: ProductUpdateManagementResponse[],
  slugToName: ReadonlyMap<string, string>
): string[] => {
  const labels: string[] = [];
  for (const update of updates) {
    if (!markdownReferencesFileName(update.markdown, fileName)) {
      continue;
    }
    const label = productUpdateAudienceLabel(
      update.permission_slug,
      slugToName
    );
    if (!labels.includes(label)) {
      labels.push(label);
    }
  }
  return labels.sort((a, b) => a.localeCompare(b));
};

type CopyableMarkdownImageReferenceProps = {
  text: string;
  onCopied: () => void;
};

const CopyableMarkdownImageReference: React.FC<
  CopyableMarkdownImageReferenceProps
> = ({ text, onCopied }) => {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      onCopied();
    } catch (error) {
      console.error('Failed to copy markdown reference:', error);
    }
  };

  return (
    <button
      type='button'
      onClick={() => void handleCopy()}
      title='Click to copy'
      aria-label={`Copy ${text}`}
      className='rounded bg-muted px-1 py-0.5 text-left text-[10px] break-all cursor-pointer transition-colors hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'
    >
      {text}
    </button>
  );
};

type ProductUpdateImageThumbnailProps = {
  fileName: string;
  thumbnailUrl?: string;
};

const THUMBNAIL_SLOT_CLASS =
  'h-11 w-11 shrink-0 overflow-hidden rounded border';

const ProductUpdateImageThumbnail: React.FC<
  ProductUpdateImageThumbnailProps
> = ({ fileName, thumbnailUrl }) => {
  const [failed, setFailed] = useState(false);

  if (!thumbnailUrl || failed) {
    return (
      <div
        className={`${THUMBNAIL_SLOT_CLASS} flex items-center justify-center bg-muted`}
        aria-hidden
      >
        <ImageIcon className='h-5 w-5 text-muted-foreground' />
      </div>
    );
  }

  return (
    <div className={`${THUMBNAIL_SLOT_CLASS} bg-muted`}>
      <img
        src={thumbnailUrl}
        alt={`Preview of ${fileName}`}
        className='h-full w-full object-cover scale-125'
        onError={() => setFailed(true)}
      />
    </div>
  );
};

type DeleteProductUpdateImageButtonProps = {
  fileName: string;
  disabled: boolean;
  disabledReason?: string;
  onDelete: () => Promise<void>;
};

const DeleteProductUpdateImageButton: React.FC<
  DeleteProductUpdateImageButtonProps
> = ({ fileName, disabled, disabledReason, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete();
      setOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={nextOpen => {
        if (!isDeleting) {
          setOpen(nextOpen);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          disabled={disabled}
          title={
            disabled
              ? (disabledReason ??
                'Remove image references from product updates before deleting')
              : `Delete ${fileName}`
          }
          aria-label={
            disabled ? `Cannot delete ${fileName}` : `Delete ${fileName}`
          }
          className='h-8 w-8 shrink-0 p-0'
        >
          <Trash2
            className={`h-4 w-4 ${disabled ? 'text-muted-foreground' : 'text-destructive'}`}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-3' align='end'>
        <div className='space-y-2'>
          <p className='text-sm font-medium'>Delete image?</p>
          <p className='text-xs text-muted-foreground'>
            Remove <span className='font-medium'>{fileName}</span> from storage.
            This cannot be undone.
          </p>
          <div className='flex justify-end gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              size='sm'
              onClick={() => void handleDelete()}
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className='h-4 w-4 mr-1 animate-spin' />}
              Delete
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export type ProductUpdatesImageManagerProps = {
  images: ProductUpdateImageResponse[];
  imageUrls: Record<string, string>;
  updates: ProductUpdateManagementResponse[];
  permissionSlugToName: ReadonlyMap<string, string>;
  onImagesChanged: () => void | Promise<void>;
  /** When this value changes, the upload form is cleared (e.g. editor or dialog context). */
  editorResetKey?: string;
};

export const ProductUpdatesImageManager: React.FC<
  ProductUpdatesImageManagerProps
> = ({
  images,
  imageUrls,
  updates,
  permissionSlugToName,
  onImagesChanged,
  editorResetKey,
}) => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );

  const [imageFileName, setImageFileName] = useState('');
  const [imageSearchQuery, setImageSearchQuery] = useState('');
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setImageFileName('');
    setImageSearchQuery('');
    setSelectedImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [editorResetKey]);

  const sortedImages = useMemo(
    () => [...images].sort((a, b) => a.file_name.localeCompare(b.file_name)),
    [images]
  );

  const audienceLabelsByFileName = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const img of images) {
      map.set(
        img.file_name,
        audienceLabelsUsingImage(img.file_name, updates, permissionSlugToName)
      );
    }
    return map;
  }, [images, updates, permissionSlugToName]);

  const filteredImages = useMemo(() => {
    return sortedImages.filter(img => {
      const usedBy = audienceLabelsByFileName.get(img.file_name) ?? [];
      const usageUiText = usedBy.length > 0 ? usedBy.join(', ') : 'Unused';
      return matchesUiSearch(imageSearchQuery, img.file_name, usageUiText);
    });
  }, [sortedImages, audienceLabelsByFileName, imageSearchQuery]);

  const existingFileNames = useMemo(
    () => new Set(images.map(img => img.file_name)),
    [images]
  );

  const trimmedImageFileName = imageFileName.trim();
  const isDuplicateFileName =
    trimmedImageFileName.length > 0 &&
    existingFileNames.has(trimmedImageFileName);

  const clearSelectedImageFile = () => {
    setSelectedImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const rejectDuplicateFileName = (fileName: string): boolean => {
    if (!existingFileNames.has(fileName)) {
      return false;
    }
    showError(
      'Duplicate File Name',
      'An image with this file name already exists. Choose a different name.'
    );
    return true;
  };

  const handleUploadImage = async () => {
    if (!selectedImageFile) {
      return;
    }
    const fileName = trimmedImageFileName;
    if (!fileName) {
      showError('Missing File Name', 'Enter a file name for the image.');
      return;
    }
    if (rejectDuplicateFileName(fileName)) {
      return;
    }

    setIsUploadingImage(true);
    try {
      await uploadProductUpdateImage(selectedImageFile, fileName);
      showSuccess(
        'Image Uploaded',
        'Reference it in markdown as ![alt](file_name).'
      );
      setImageFileName('');
      setSelectedImageFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      await onImagesChanged();
    } catch (error) {
      console.error('Failed to upload product update image:', error);
      showError('Upload Failed', 'Unable to upload image. Please try again.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleDeleteImage = async (image: ProductUpdateImageResponse) => {
    setDeletingImageId(image.id);
    try {
      await deleteProductUpdateImage(image.id);
      showSuccess('Image Deleted', `${image.file_name} was removed.`);
      await onImagesChanged();
    } catch (error) {
      console.error('Failed to delete product update image:', error);
      showError('Delete Failed', 'Unable to delete image. Please try again.');
    } finally {
      setDeletingImageId(null);
    }
  };

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-3'>
      <p className='shrink-0 text-xs text-muted-foreground'>
        Shared across all product updates. Embed with{' '}
        <code className='rounded bg-muted px-1 py-0.5 text-[10px]'>
          ![alt](file_name)
        </code>
        — click a reference below to copy.
      </p>

      <Input
        inputSize='sm'
        value={imageSearchQuery}
        onChange={e => setImageSearchQuery(e.target.value)}
        placeholder='Search by file name or audience…'
        aria-label='Search images'
        className='shrink-0'
      />

      <div className='min-h-0 flex-1 overflow-y-auto rounded-md border'>
        {sortedImages.length > 0 ? (
          filteredImages.length > 0 ? (
            <ul className='divide-y'>
              {filteredImages.map(img => {
                const usedBy =
                  audienceLabelsByFileName.get(img.file_name) ?? [];
                const isInUse = usedBy.length > 0;
                const isDeletingThis = deletingImageId === img.id;
                return (
                  <li
                    key={img.id}
                    className='flex items-center gap-2 px-2 py-1 text-xs'
                  >
                    <ProductUpdateImageThumbnail
                      fileName={img.file_name}
                      thumbnailUrl={imageUrls[img.file_name]}
                    />
                    <div className='min-w-0 flex-1 space-y-0.5 leading-tight'>
                      <p className='font-medium truncate' title={img.file_name}>
                        {img.file_name}
                      </p>
                      <CopyableMarkdownImageReference
                        text={markdownImageReference(img.file_name)}
                        onCopied={() => showSuccess('Text copied')}
                      />
                      {isInUse ? (
                        <p className='text-muted-foreground truncate'>
                          Used by {usedBy.join(', ')}
                        </p>
                      ) : (
                        <p className='text-muted-foreground italic'>Unused</p>
                      )}
                    </div>
                    <DeleteProductUpdateImageButton
                      fileName={img.file_name}
                      disabled={isInUse || isDeletingThis}
                      disabledReason={
                        isInUse
                          ? `Used by ${usedBy.join(', ')}. Remove markdown references first.`
                          : undefined
                      }
                      onDelete={() => handleDeleteImage(img)}
                    />
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className='px-2 py-2 text-xs text-muted-foreground'>
              No images match your search.
            </p>
          )
        ) : (
          <p className='px-2 py-2 text-xs text-muted-foreground'>
            No images yet.
          </p>
        )}
      </div>

      <div className='shrink-0 space-y-2 border-t pt-3'>
        <p className='text-xs font-medium'>Upload</p>
        <input
          ref={fileInputRef}
          id='product-update-image-file'
          type='file'
          accept='image/jpeg,image/png,image/gif,image/webp'
          disabled={isUploadingImage}
          className='block w-full cursor-pointer text-xs text-foreground file:mr-2 file:cursor-pointer file:rounded file:border file:border-input file:bg-background file:px-2 file:py-1 file:text-xs file:font-medium hover:file:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50'
          onChange={e => {
            const file = e.target.files?.[0] ?? null;
            if (!file) {
              setSelectedImageFile(null);
              return;
            }
            const derivedName = markdownFileNameFromSelectedFile(file.name);
            const fileName =
              trimmedImageFileName === '' ? derivedName : trimmedImageFileName;
            if (rejectDuplicateFileName(fileName)) {
              clearSelectedImageFile();
              if (trimmedImageFileName === '') {
                setImageFileName(derivedName);
              }
              return;
            }
            setSelectedImageFile(file);
            if (trimmedImageFileName === '') {
              setImageFileName(derivedName);
            }
          }}
        />
        <div className='flex flex-wrap items-end gap-2'>
          <div className='min-w-[140px] flex-1 space-y-1'>
            <Label htmlFor='product-update-image-file-name' className='text-xs'>
              Markdown file name
            </Label>
            <Input
              id='product-update-image-file-name'
              inputSize='sm'
              value={imageFileName}
              onChange={e => setImageFileName(e.target.value)}
              onKeyDown={e => {
                if (e.key !== 'Enter') {
                  return;
                }
                e.preventDefault();
                if (
                  isUploadingImage ||
                  !selectedImageFile ||
                  !trimmedImageFileName ||
                  isDuplicateFileName
                ) {
                  return;
                }
                void handleUploadImage();
              }}
              placeholder='hero-screenshot'
            />
          </div>
          <Button
            type='button'
            size='sm'
            variant='outline'
            disabled={
              isUploadingImage ||
              !selectedImageFile ||
              !trimmedImageFileName ||
              isDuplicateFileName
            }
            title={
              isDuplicateFileName
                ? 'An image with this file name already exists'
                : undefined
            }
            onClick={() => void handleUploadImage()}
          >
            {isUploadingImage ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <ImagePlus className='h-4 w-4' />
            )}
          </Button>
        </div>
        {isDuplicateFileName ? (
          <p className='text-[10px] text-destructive'>
            An image named &quot;{trimmedImageFileName}&quot; already exists.
            Choose a different file name.
          </p>
        ) : null}
        {trimmedImageFileName && !isDuplicateFileName ? (
          <p className='text-[10px] text-muted-foreground'>
            <CopyableMarkdownImageReference
              text={markdownImageReference(trimmedImageFileName)}
              onCopied={() => showSuccess('Text copied')}
            />
          </p>
        ) : null}
      </div>
    </div>
  );
};
